import { useEffect, useState } from "react"
import { Alert, Box, Button, IconButton, Paper, Stack, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActiveOutlined"
import { useNotifications } from "../context/NotificationContext"
import { SUPPRESSED_PROMPT_HELP } from "../realtime/push"
import { MOTION, transition } from "../theme"

const DISMISSED_KEY = "tallywise.desktopNotifications.dismissedUntil"

/**
 * Asks once, from a click, and takes no for an answer.
 *
 * ── Why this is a banner and not a call on page load ────────────────────────
 *
 * `Notification.requestPermission()` without a user gesture is blocked outright
 * by some browsers and counted against the site by others. Worse, a denial is
 * sticky: the user has to go into site settings to undo it, which nobody does. So
 * the prompt is a button, shown after a delay, and dismissing it is remembered.
 *
 * The delay matters too — a permission prompt in the first second of a session is
 * the most-refused prompt on the web, because the person has not yet decided
 * whether they care about this app.
 *
 * ── Why "not now" wears off ─────────────────────────────────────────────────
 *
 * It used to be forever, which made "not now" mean "never" — and the only way
 * back was a card most people never open. A fortnight is long enough that it is
 * not nagging and short enough that somebody who has since started relying on
 * the app gets asked again.
 *
 * ── Why a browser that says nothing is the interesting case ─────────────────
 *
 * Edge ships quiet notification requests switched on, so the request resolves
 * with nothing shown and the permission still unset. Closing the banner at that
 * point is indistinguishable from the feature being broken, which is exactly
 * what it was reported as.
 */
const SHOW_AFTER_MS = 20000
const DISMISS_FOR_MS = 14 * 24 * 60 * 60 * 1000

export default function DesktopNotificationPrompt() {
  const { permission, enableDesktopNotifications } = useNotifications()
  const [visible, setVisible] = useState(false)
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState(null)

  useEffect(() => {
    if (permission !== "default") return undefined
    try {
      const until = Number(localStorage.getItem(DISMISSED_KEY) || 0)
      if (Number.isFinite(until) && until > Date.now()) return undefined
    } catch {
      // Blocked storage: show it, at worst they dismiss it again.
    }
    const timer = setTimeout(() => setVisible(true), SHOW_AFTER_MS)
    return () => clearTimeout(timer)
  }, [permission])

  const dismiss = () => {
    setVisible(false)
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now() + DISMISS_FOR_MS))
    } catch {
      /* the dismissal lasts for this tab */
    }
  }

  if (!visible || permission !== "default") return null

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        position: "fixed",
        bottom: 24,
        left: 24,
        zIndex: theme.zIndex.snackbar,
        p: 2,
        maxWidth: 400,
        borderRadius: 2.5,
        border: 1,
        borderColor: "divider",
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 1px 3px rgba(0,0,0,0.5), 0 14px 32px -10px rgba(0,0,0,0.6)"
            : "0 1px 3px rgba(16,35,10,0.08), 0 14px 32px -10px rgba(16,35,10,0.24)",
        transition: transition("box-shadow", MOTION.fast),
      })}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{ color: "primary.dark", pt: 0.25 }}>
          <NotificationsActiveIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={650} gutterBottom>
            Turn on desktop notifications
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Get messages and task updates even when TallyWise is not the tab you are looking at.
          </Typography>

          {/* The browser said nothing. Saying nothing back is how this reads as
              a broken button. */}
          {outcome === "suppressed" ? (
            <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
              <Typography variant="caption">{SUPPRESSED_PROMPT_HELP}</Typography>
            </Alert>
          ) : null}
          {outcome === "denied" ? (
            <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
              <Typography variant="caption">
                Notifications are blocked for this site. Only the browser can undo that — TallyWise cannot ask again.
              </Typography>
            </Alert>
          ) : null}
          {outcome === "unsupported" ? (
            <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
              <Typography variant="caption">This browser does not support desktop notifications.</Typography>
            </Alert>
          ) : null}

          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              disabled={busy}
              onClick={async () => {
                setBusy(true)
                const result = await enableDesktopNotifications()
                setBusy(false)
                setOutcome(result)
                // Only close on a real answer. A suppressed request has to leave
                // the explanation on screen, next to the button that produced
                // nothing.
                if (result === "granted") setVisible(false)
              }}
            >
              {busy ? "Asking…" : outcome === "suppressed" ? "Try again" : "Turn on"}
            </Button>
            <Button size="small" color="inherit" onClick={dismiss}>
              Not now
            </Button>
          </Stack>
        </Box>
        <IconButton size="small" onClick={dismiss} aria-label="Dismiss" sx={{ mt: -0.5, mr: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  )
}
